import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ClassStudentSelect } from "@/components/ClassStudentSelect";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, CheckCircle2, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const SECTIONS = [
  { key: "paragraf", title: "PARAGRAF", labelCol: "DENEME" },
  { key: "deyim_atasozu", title: "DEYİM & ATASÖZÜ", labelCol: "ÜNİTE" },
  { key: "ingilizce_kelime", title: "İNGİLİZCE KELİME EZBER", labelCol: "ÜNİTE" },
  { key: "turkce_sozluk", title: "TÜRKÇE SÖZLÜK", labelCol: "KONU" },
  { key: "kitap", title: "KİTAP", labelCol: "İSİM" },
];

const MONTHS_TR = { "01": "OCAK", "02": "ŞUBAT", "03": "MART", "04": "NİSAN", "05": "MAYIS", "06": "HAZİRAN", "07": "TEMMUZ", "08": "AĞUSTOS", "09": "EYLÜL", "10": "EKİM", "11": "KASIM", "12": "ARALIK" };
const fmtDate = (d) => d ? d.split("-").reverse().join(".") : "—";
const monthLabel = (m) => { const [y, mo] = m.split("-"); return `${MONTHS_TR[mo] || mo} ${y}`; };

export default function MonthlyEval() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isPicker = user?.role === "admin" || user?.role === "koc";
  const isAdmin = user?.role === "admin";
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [students, setStudents] = useState([]);
  const [month, setMonth] = useState("2026-09");
  const [months, setMonths] = useState([]);
  const [doc, setDoc] = useState(null);
  const [addFor, setAddFor] = useState(null); // section key
  const [row, setRow] = useState({ date: "", label: "", dogru: "", yanlis: "", bos: "" });

  useEffect(() => { if (isPicker) api.get("/students").then(({ data }) => setStudents(data)); }, [isPicker]);
  useEffect(() => {
    if (studentId || isPicker) return;
    api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); setStudentName(data.student_name || ""); });
  }, [studentId, isPicker]);

  const load = useCallback(async () => {
    if (!studentId) return;
    const [{ data: d }, { data: ms }] = await Promise.all([
      api.get("/monthly-eval", { params: { student_id: studentId, month } }),
      api.get("/monthly-eval/months", { params: { student_id: studentId } }),
    ]);
    setDoc(d);
    setMonths(ms);
  }, [studentId, month]);

  useEffect(() => { load(); }, [load]);

  const save = async (sections) => {
    try {
      const { data } = await api.post("/monthly-eval", { student_id: studentId, month, sections });
      setDoc(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sections = doc?.sections || {};

  const addRow = async () => {
    if (!row.label.trim()) return toast.error("İsim/ünite gerekli");
    const next = { ...sections };
    next[addFor] = [...(next[addFor] || []), { date: row.date, label: row.label.trim(), dogru: Number(row.dogru) || 0, yanlis: Number(row.yanlis) || 0, bos: Number(row.bos) || 0, approved: false }];
    await save(next);
    setAddFor(null);
    setRow({ date: "", label: "", dogru: "", yanlis: "", bos: "" });
    toast.success("Satır eklendi ✓");
  };

  const removeRow = async (sec, i) => {
    const next = { ...sections, [sec]: sections[sec].filter((_, j) => j !== i) };
    await save(next);
    toast.success("Satır silindi");
  };

  const approveRow = async (sec, i, approved) => {
    try {
      const { data } = await api.patch("/monthly-eval/approve", { student_id: studentId, month, section: sec, index: i, approved });
      setDoc(data);
      toast.success(approved ? "Onaylandı ✓" : "Onay kaldırıldı");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 no-print">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600"><ClipboardList className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Aylık Değerlendirme</h1>
            <p className="text-slate-500">Ay bazlı paragraf, deyim-atasözü, İngilizce kelime, sözlük ve kitap takibi.{studentName ? ` · ${studentName}` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400" />
          {months.filter((m) => m !== month).slice(0, 3).map((m) => (
            <button key={m} onClick={() => setMonth(m)} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-indigo-400">{monthLabel(m)}</button>
          ))}
          <button data-testid="eval-print-btn" onClick={() => window.print()} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 font-semibold text-slate-700 transition hover:shadow-md active:scale-95"><Printer className="h-5 w-5" /> Yazdır / PDF</button>
        </div>
      </div>

      {isPicker && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm no-print">
          <label className="mb-1 block text-sm font-semibold text-slate-700">Öğrenci Seç</label>
          <ClassStudentSelect students={students} value={studentId} onChange={(v) => { setStudentId(v); setStudentName(students.find((s) => s.id === v)?.name || ""); }} testid="eval-student-select" placeholder="Seçiniz…" />
        </div>
      )}

      {!studentId ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Öğrenci seçin.</div>
      ) : (
        <>
          {/* Fotoğraftaki başlık bloğu */}
          <div className="text-center">
            <div className="font-display text-xl font-extrabold tracking-wide text-slate-900">ZOR NEW GENERATION ACADEMY</div>
            <div className="mt-1 font-display text-lg font-bold text-slate-700">{monthLabel(month)} AYI GENEL DEĞERLENDİRME TABLOSU</div>
            {studentName && <div className="mx-auto mt-3 max-w-md rounded-lg bg-[#1E293B] py-2 font-display text-lg font-bold uppercase tracking-widest text-white">{studentName}</div>}
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            {SECTIONS.slice(0, 4).map((sec) => (
              <SectionTable key={sec.key} sec={sec} rows={sections[sec.key] || []} isAdmin={isAdmin} onAdd={() => setAddFor(sec.key)} onRemove={(i) => removeRow(sec.key, i)} onApprove={(i, ap) => approveRow(sec.key, i, ap)} />
            ))}
            <div className="lg:col-span-2">
              <SectionTable sec={SECTIONS[4]} rows={sections.kitap || []} isAdmin={isAdmin} onAdd={() => setAddFor("kitap")} onRemove={(i) => removeRow("kitap", i)} onApprove={(i, ap) => approveRow("kitap", i, ap)} />
            </div>
          </div>
        </>
      )}

      <Dialog open={!!addFor} onOpenChange={(o) => !o && setAddFor(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{addFor && SECTIONS.find((s) => s.key === addFor)?.title} — Satır Ekle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Tarih</label><input data-testid="eval-row-date" type="date" value={row.date} onChange={(e) => setRow({ ...row, date: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{addFor ? SECTIONS.find((s) => s.key === addFor)?.labelCol : "İSİM"}</label><input data-testid="eval-row-label" value={row.label} onChange={(e) => setRow({ ...row, label: e.target.value })} placeholder="ör. 3. DENEME" className={inp} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className={lbl}>Doğru</label><input data-testid="eval-row-d" type="number" min={0} value={row.dogru} onChange={(e) => setRow({ ...row, dogru: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>Yanlış</label><input data-testid="eval-row-y" type="number" min={0} value={row.yanlis} onChange={(e) => setRow({ ...row, yanlis: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>Boş</label><input data-testid="eval-row-b" type="number" min={0} value={row.bos} onChange={(e) => setRow({ ...row, bos: e.target.value })} className={inp} /></div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="eval-row-save" onClick={addRow} className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95">Ekle</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionTable({ sec, rows, isAdmin, onAdd, onRemove, onApprove }) {
  return (
    <div data-testid={`eval-section-${sec.key}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between bg-[#1E293B] px-4 py-2.5">
        <h3 className="font-display text-sm font-bold uppercase tracking-widest text-white">{sec.title}</h3>
        {isAdmin && <button data-testid={`eval-add-${sec.key}`} onClick={onAdd} className="flex items-center gap-1 rounded-lg bg-white/10 px-2.5 py-1 text-xs font-bold text-white transition hover:bg-white/20 active:scale-95 no-print"><Plus className="h-3.5 w-3.5" /> Satır Ekle</button>}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-[11px] font-bold uppercase text-slate-500">
            <th className="px-3 py-2 text-left">Tarih</th>
            <th className="px-3 py-2 text-left">{sec.labelCol}</th>
            <th className="px-2 py-2 text-center">Doğru</th>
            <th className="px-2 py-2 text-center">Yanlış</th>
            <th className="px-2 py-2 text-center">Boş</th>
            <th className="px-2 py-2 text-center">Net</th>
            {isAdmin && <th className="px-2 py-2 text-center no-print">İşlem</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Kayıt yok</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} data-testid={`eval-row-${sec.key}-${i}`} className={`transition ${r.approved ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
              <td className="px-3 py-2 font-mono text-xs text-slate-500">{fmtDate(r.date)}</td>
              <td className="px-3 py-2 font-semibold text-slate-800">{r.label}</td>
              <td className="px-2 py-2 text-center font-bold text-emerald-600">{r.dogru || "—"}</td>
              <td className="px-2 py-2 text-center font-bold text-rose-600">{r.yanlis || "—"}</td>
              <td className="px-2 py-2 text-center font-bold text-slate-500">{r.bos ?? "—"}</td>
              <td className="px-2 py-2 text-center font-mono font-bold text-slate-700">{r.net ?? "—"}</td>
              {isAdmin && (
                <td className="px-2 py-2 text-center no-print">
                  <button data-testid={`eval-approve-${sec.key}-${i}`} onClick={() => onApprove(i, !r.approved)} title={r.approved ? "Onayı kaldır" : "Onayla"} className={`rounded-lg p-1.5 transition active:scale-90 ${r.approved ? "bg-emerald-500 text-white" : "text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"}`}>
                    <CheckCircle2 className="h-4 w-4" />
                  </button>
                  <button data-testid={`eval-remove-${sec.key}-${i}`} onClick={() => onRemove(i)} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 active:scale-90"><Trash2 className="h-4 w-4" /></button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const lbl = "mb-1 block text-sm font-semibold text-slate-700";
const inp = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";
